export function isSuperAdmin(user) {
  return user?.role_name === 'super_admin';
}

export function isProtectedSuperAdmin(userOrEmail) {
  const email = typeof userOrEmail === 'string'
    ? userOrEmail
    : userOrEmail?.email;

  return email?.toLowerCase() === 'sshankha.123@gmail.com';
}

export function isLockedSuperAdmin(user) {
  return isSuperAdmin(user) || isProtectedSuperAdmin(user);
}

export function canAccessUsersModule(user) {
  if (!user?.role_name) {
    return false;
  }

  if (isSuperAdmin(user) || user.role_name === 'admin') {
    return true;
  }

  return user.role_name === 'auditor' && user.is_manager === true;
}

export function canAccessAuditsModule(user) {
  return canAccessUsersModule(user);
}

/** Organization / outlet (client data) management: Admin only. */
export function canAccessOrganizationsModule(user) {
  return isAdminOrAbove(user);
}

export function canAccessOutletsModule(user) {
  return isAdminOrAbove(user);
}

/** Super Admin or Admin. */
export function isAdminOrAbove(user) {
  return isSuperAdmin(user) || user?.role_name === 'admin';
}

/** Scanning members use the handheld (HHT) app only, not the web panel. */
export function isScanningMember(user) {
  return user?.role_name === 'auditor' && !user.is_manager;
}

export function canAccessTeamsModule(user) {
  return canAccessUsersModule(user);
}

/** Clients page: only roles that may create client users. */
export function canAccessClientsModule(user) {
  return canAssignRole(user, 'client');
}

export function canAccessDevicesModule(user) {
  if (!user?.role_name) {
    return false;
  }

  return isSuperAdmin(user) || user.role_name === 'admin';
}

export function canManageUser(actor, target) {
  if (!actor || !target) {
    return false;
  }

  if (isLockedSuperAdmin(target)) {
    return false;
  }

  if (isSuperAdmin(actor)) {
    return true;
  }

  if (actor.role_name === 'admin') {
    return ['auditor', 'client'].includes(target.role_name);
  }

  // A Data ASA manages scanning members only; team leads (Data ASAs) are
  // added and changed by an Admin.
  if (actor.role_name === 'auditor' && actor.is_manager) {
    return target.role_name === 'auditor' && !target.is_manager;
  }

  return false;
}

export function canAssignRole(actor, roleName) {
  if (!actor || !roleName) {
    return false;
  }

  if (isSuperAdmin(actor)) {
    return ['admin', 'auditor', 'client'].includes(roleName);
  }

  if (actor.role_name === 'admin') {
    return ['auditor', 'client'].includes(roleName);
  }

  if (actor.role_name === 'auditor' && actor.is_manager) {
    return roleName === 'auditor';
  }

  return false;
}

export function canSetManagerFlag(actor) {
  return isSuperAdmin(actor) || actor?.role_name === 'admin';
}

export function getAssignableRoles(actor, roles) {
  return roles.filter((role) => canAssignRole(actor, role.name));
}

// Names shown in the panel. The stored role names (super_admin, admin,
// auditor, client) do not change; an auditor with the manager / team lead
// tick is a "Data ASA", one without it a "Scanning Member".
export const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  auditor: 'Scanning Member',
  client: 'Client',
};

export const DATA_ASA_LABEL = 'Data ASA';

/** Display name for a stored role name (role picker options). */
export function getRoleNameLabel(roleName) {
  return ROLE_LABELS[roleName] || roleName || 'Unassigned';
}

export function getRoleLabel(user) {
  if (!user?.role_name) {
    return 'Unassigned';
  }

  if (user.role_name === 'auditor' && user.is_manager) {
    return DATA_ASA_LABEL;
  }

  return getRoleNameLabel(user.role_name);
}
